/**
 * useStreamingChat — SSOT state machine for a single chat conversation.
 *
 * Encapsulates:
 *  - `AbortController` lifecycle (so `stop()` genuinely aborts the fetch)
 *  - whole-thread state replacement on every `messages_update` frame
 *    (matches BE's develop/main wire shape — full thread per OpenAI chunk
 *    with `streaming: true/false` on the in-flight assistant)
 *  - optimistic user-message insertion with a `client_nonce` for idempotency
 *  - `retry` / `regenerate` actions
 *
 * Parents never touch SSE or AbortController; they just call `send()` and
 * render the returned `messages` + `state`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message } from '../../App';
import {
    cryptoRandomNonce,
    regenerateLastTurn,
    streamChatResponse,
    type QuotaEvent,
    type StreamErrorEvent,
    type StreamHandlers,
} from './chatservice';
import {
    trackChatSend,
    trackChatStop,
    trackChatRegenerate,
    trackChatRetry,
    trackQuotaWarning,
    trackQuotaExhausted,
} from '../../analytics';

export type ChatState =
    | 'idle'
    | 'submitting'
    | 'streaming'
    | 'completed'
    | 'cancelled'
    | 'error';

type UseStreamingChatArgs = {
    chatId: string | null;
    /** Initial messages already loaded (from GET /messages/). */
    initialMessages?: Message[];
    /** Called when usage would benefit from a refresh in the rest of the app
     *  (profile badge, access snapshot, quota banner). */
    onUsageRefresh?: () => void;
    /** Called when the quota threshold is crossed mid-stream. */
    onQuotaWarning?: (ev: QuotaEvent) => void;
    /** Called when the hard cap is hit — FE should show the upgrade modal. */
    onQuotaExhausted?: (ev: QuotaEvent) => void;
};

export type SendOptions = {
    /** Explicit chatId override — required when the chat was just created
     *  this tick and hasn't propagated through the `chatId` prop yet
     *  (common on the first-message-creates-chat flow in App.tsx). */
    chatId?: string | null;
};

export type UseStreamingChatReturn = {
    state: ChatState;
    messages: Message[];
    lastError: StreamErrorEvent | null;
    /** Replace the local messages — used when a parent switches chats. */
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    send: (content: string, file?: File | null, opts?: SendOptions) => Promise<void>;
    stop: () => void;
    retry: () => Promise<void>;
    regenerate: () => Promise<void>;
};

export function useStreamingChat({
    chatId,
    initialMessages = [],
    onUsageRefresh,
    onQuotaWarning,
    onQuotaExhausted,
}: UseStreamingChatArgs): UseStreamingChatReturn {
    const [messages, setMessages] = useState<Message[]>(initialMessages);
    const [state, setState] = useState<ChatState>('idle');
    const [lastError, setLastError] = useState<StreamErrorEvent | null>(null);

    // AbortController is kept in a ref so `stop` can reach the current
    // in-flight fetch without re-rendering on every state flip.
    const abortRef = useRef<AbortController | null>(null);
    // Last user-turn payload — lets `retry` resend without a fresh `send` call.
    const lastPayloadRef = useRef<{ content: string; file: File | null } | null>(null);

    // Abort any in-flight stream ONLY on hook unmount.
    // Do NOT depend on `chatId` here: when the user sends the first
    // message from the welcome screen we synchronously create a chat,
    // call setSelectedChatId(), and then call sendStream({ chatId }).
    // The setSelectedChatId triggers a re-render, and if that re-render
    // fires a cleanup keyed on chatId the freshly-started fetch gets
    // aborted before its first byte — the user sees the optimistic
    // bubble and nothing else. Empty deps is the correct contract.
    useEffect(() => {
        return () => {
            abortRef.current?.abort();
            abortRef.current = null;
        };
    }, []);

    /** Shared event-handler factory — send() and regenerate() both use the
     *  same dispatch table. Develop/main wire: every messages_update
     *  replaces messages state wholesale. The server's `streaming: true/false`
     *  flag on each message drives MessageBubble's typing dot. */
    const buildHandlers = useCallback((): StreamHandlers => ({
        onMessagesUpdated: (thread) => {
            setState('streaming');
            setMessages(thread);
        },
        onQuotaWarning: (ev) => {
            const fraction = (ev.used ?? 0) / Math.max(ev.limit ?? 1, 1);
            trackQuotaWarning(fraction);
            onQuotaWarning?.(ev);
        },
        onQuotaExhausted: (ev) => {
            trackQuotaExhausted();
            onQuotaExhausted?.(ev);
        },
        onError: (err) => {
            setLastError(err);
        },
        onDone: () => {
            // Outer try/catch resolves state on promise return.
        },
    }), [onQuotaWarning, onQuotaExhausted]);

    /** Handle the post-stream state transitions + error path. Shared by
     *  send() and regenerate(). */
    const handleStreamOutcome = useCallback((err: unknown | null) => {
        if (err === null) {
            setState((s) => (s === 'error' ? 'error' : 'completed'));
            onUsageRefresh?.();
            return;
        }
        const code = (err as Error & { code?: string }).code;
        if (code === 'user_aborted') {
            // Flip any still-streaming bubble locally. The server also
            // persists the partial via finalize_partial_cancelled; the
            // next messages GET returns the authoritative row.
            setMessages((prev) => prev.map((m) =>
                m.streaming ? { ...m, streaming: false, status: 'cancelled' } : m
            ));
            setState('cancelled');
            return;
        }
        const errPayload: StreamErrorEvent = {
            message: (err as Error).message || 'Stream failed',
        };
        if (code) errPayload.code = code;
        setLastError(errPayload);
        setState('error');
    }, [onUsageRefresh]);

    const handleSend = useCallback(
        async (content: string, file: File | null, opts?: SendOptions) => {
            // `opts.chatId` lets the caller override the hook's `chatId`
            // prop on the same tick a chat is created — React state updates
            // don't land in the hook closure until the next render.
            const activeChatId = opts?.chatId ?? chatId;
            if (!activeChatId) return;
            if (state === 'submitting' || state === 'streaming') {
                // Guard against double-click.
                return;
            }
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            const nonce = cryptoRandomNonce();

            lastPayloadRef.current = { content, file };
            setLastError(null);
            setState('submitting');
            trackChatSend({ chatId: activeChatId, length: content.length });

            // Optimistic user bubble — replaced when the server's first
            // messages_update arrives with the committed row.
            const tempUserId = `tmp-${nonce}`;
            setMessages((prev) => [
                ...prev,
                {
                    id: tempUserId,
                    order: prev.length,
                    role: 'user',
                    content,
                    ...(file
                        ? {
                            files: [{
                                id: `tmp-file-${nonce}`,
                                file: '',
                                original_name: file.name,
                                file_type: file.type,
                            }],
                        }
                        : {}),
                },
            ]);

            try {
                await streamChatResponse(
                    activeChatId,
                    { content, client_nonce: nonce, file },
                    buildHandlers(),
                    controller.signal,
                );
                handleStreamOutcome(null);
            } catch (err) {
                handleStreamOutcome(err);
            }
        },
        [chatId, state, buildHandlers, handleStreamOutcome],
    );

    const send = useCallback(
        (content: string, file?: File | null, opts?: SendOptions) =>
            handleSend(content, file ?? null, opts),
        [handleSend],
    );

    const stop = useCallback(() => {
        if (abortRef.current) {
            trackChatStop();
            abortRef.current.abort();
            abortRef.current = null;
        }
    }, []);

    const retry = useCallback(async () => {
        const last = lastPayloadRef.current;
        if (!last) return;
        trackChatRetry();
        // Drop any failed/cancelled assistant bubble locally so the new turn
        // replaces it cleanly; the backend creates a fresh row.
        setMessages((prev) => {
            const idx = [...prev].reverse().findIndex(
                (m) => m.role === 'assistant' && (m.status === 'error' || m.status === 'cancelled'),
            );
            if (idx === -1) return prev;
            const realIdx = prev.length - 1 - idx;
            return prev.filter((_, i) => i !== realIdx);
        });
        await handleSend(last.content, last.file);
    }, [handleSend]);

    const regenerate = useCallback(async () => {
        if (!chatId) return;
        if (state === 'submitting' || state === 'streaming') return;
        trackChatRegenerate();
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLastError(null);
        setState('submitting');
        // Drop the last assistant turn locally so the UI doesn't double-render
        // while the server prunes + regenerates.
        setMessages((prev) => {
            for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i]!.role === 'assistant') {
                    return prev.slice(0, i);
                }
            }
            return prev;
        });

        try {
            await regenerateLastTurn(chatId, buildHandlers(), controller.signal);
            handleStreamOutcome(null);
        } catch (err) {
            handleStreamOutcome(err);
        }
    }, [chatId, state, buildHandlers, handleStreamOutcome]);

    return {
        state,
        messages,
        lastError,
        setMessages,
        send,
        stop,
        retry,
        regenerate,
    };
}
